// El armazón de una pantalla a sangre completa: cancela el aire del `AppShell` desde
// `SHELL_CONTENT_FULL_BLEED` y apila barra, avisos y cuerpo pegados, sin márgenes sueltos.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { SHELL_CONTENT_FULL_BLEED } from '../AppShell';

/** `fill` fija el alto y el cuerpo scrollea adentro; `grow` deja crecer la página y scrollea ella. */
export type ScreenFrameHeight = 'fill' | 'grow';

/** El armazón como clase suelta, para la pantalla cuyo root ya es otro componente (un boundary). */
export function screenFrameClass(height: ScreenFrameHeight = 'fill', className?: string): string {
    return cn('flex flex-col', SHELL_CONTENT_FULL_BLEED[height], className);
}

export interface ScreenFrameProps {
    children: ReactNode;
    height?: ScreenFrameHeight;
    className?: string;
}

export function ScreenFrame({ children, height = 'fill', className }: ScreenFrameProps) {
    return <div className={screenFrameClass(height, className)}>{children}</div>;
}

/** La barra superior: pegada arriba, con su borde. El aire y la alineación los pone quien la usa. */
export function ScreenFrameBar({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={cn('flex flex-none items-stretch border-b border-border bg-card', className)}
        >
            {children}
        </div>
    );
}

export type ScreenFrameNoticeTone = 'neutral' | 'warning' | 'danger';

// El tono pinta el fondo y nada más: el color del texto lo decide el contenido del aviso.
const NOTICE_TONE: Record<ScreenFrameNoticeTone, string> = {
    neutral: 'bg-muted/25',
    warning: 'bg-warning/10',
    danger: 'bg-destructive/5',
};

export interface ScreenFrameNoticeProps {
    children: ReactNode;
    tone?: ScreenFrameNoticeTone;
    /** `status` para el aviso que aparece solo; `region` para el permanente, que necesita nombre. */
    role?: 'status' | 'region';
    label?: string;
    className?: string;
}

/**
 * Una banda de aviso, pegada a lo que tiene arriba y abajo y separada solo por su borde. Nunca lleva
 * margen propio: dos avisos apilados tienen que leerse como una pila, no como dos tarjetas sueltas.
 */
export function ScreenFrameNotice({
    children,
    tone = 'neutral',
    role,
    label,
    className,
}: ScreenFrameNoticeProps) {
    return (
        <div
            role={role}
            aria-label={label}
            className={cn(
                'flex-none border-b border-border px-3 py-2 text-[11.5px]',
                NOTICE_TONE[tone],
                className,
            )}
        >
            {children}
        </div>
    );
}

/** El cuerpo: ocupa el alto restante sin tocar el scroll de lo que cuelgue adentro. */
export function ScreenFrameBody({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return <div className={cn('flex min-h-0 flex-1 flex-col p-2', className)}>{children}</div>;
}
